import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Minus, Phone, BookOpen, HeartPulse, Activity, Salad, Bone, Microscope, ArrowRight } from 'lucide-react';
import { CLINIC, EDUCATION, IMAGES, EDU_ARTICLES } from '../mock';
import { PageHero, SectionTitle, Reveal } from '../components/Shared';

const TOPICS = [
  { icon: HeartPulse, title: 'Autoimmune Disease', body: 'Understand how the immune system mistakenly attacks the body and what that means for you.' },
  { icon: Activity, title: 'Joint Care', body: 'Simple daily habits and exercises to protect your joints and stay mobile.' },
  { icon: Salad, title: 'Anti-inflammatory Diet', body: 'Food choices that can support your treatment and reduce flare frequency.' },
  { icon: Bone, title: 'Bone Health', body: 'Osteoporosis prevention, calcium, vitamin D and screening after 50.' },
  { icon: Microscope, title: 'Understanding Tests', body: 'What RF, Anti-CCP, ANA, ESR and CRP mean for your diagnosis.' },
  { icon: BookOpen, title: 'Treatment Options', body: 'From DMARDs to biologics — how modern therapy controls disease.' },
];

export default function PatientEducation() {
  const [open, setOpen] = useState(0);

  return (
    <div>
      <PageHero eyebrow="Patient Education" title="Understand your condition, own your care" subtitle="Clear, reliable information to help you make confident decisions about your health." image={IMAGES.lab} />

      <section className="py-20">
        <div className="container-x">
          <SectionTitle center eyebrow="Learn" title="Explore key topics" />
          <div className="grid md:grid-cols-3 sm:grid-cols-2 gap-6">
            {TOPICS.map((t, i) => (
              <Reveal key={t.title} delay={(i % 3) * 80}>
                <div className="card-lift bg-white border border-teal-50 rounded-2xl p-7 h-full">
                  <span className="grid place-items-center w-14 h-14 rounded-2xl bg-teal-50 text-teal-700 mb-5"><t.icon size={26} /></span>
                  <h3 className="text-lg text-teal-900 mb-2" style={{ fontWeight: 600 }}>{t.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{t.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Featured in-depth articles */}
      <section className="mint-section py-20">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <SectionTitle eyebrow="In-Depth Guides" title="Answers to common patient questions" sub="Detailed, easy-to-understand articles written by our specialists." />
            <Link to="/ask" className="btn-teal inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">Ask a Rheumatologist <ArrowRight size={16} /></Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {EDU_ARTICLES.map((a, i) => (
              <Reveal key={a.slug} delay={(i % 4) * 70}>
                <Link to={`/patient-education/${a.slug}`} className="group card-lift block bg-white rounded-2xl overflow-hidden border border-teal-50 h-full">
                  <div className="h-36 overflow-hidden">
                    <img src={IMAGES[a.image]} alt={a.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <span className="text-xs font-semibold text-teal-600 uppercase tracking-wide">{a.eyebrow}</span>
                    <h3 className="text-base text-teal-900 mt-1.5 leading-snug group-hover:text-teal-700 transition-colors" style={{ fontWeight: 600 }}>{a.title}</h3>
                    <span className="inline-flex items-center gap-1 text-teal-700 text-sm font-medium mt-3">Read <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-x max-w-3xl">
          <SectionTitle center eyebrow="FAQ" title="Frequently asked questions" />
          <div className="space-y-4">
            {EDUCATION.map((e, i) => {
              const isOpen = open === i;
              return (
                <div key={e.title} className={`bg-white rounded-2xl border transition-colors ${isOpen ? 'border-teal-200' : 'border-teal-50'}`}>
                  <button onClick={() => setOpen(isOpen ? -1 : i)} className="flex items-center justify-between w-full text-left p-6">
                    <span className="font-semibold text-teal-900 pr-4">{e.title}</span>
                    <span className={`grid place-items-center w-8 h-8 rounded-full shrink-0 transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-teal-50 text-teal-700'}`}>{isOpen ? <Minus size={16} /> : <Plus size={16} />}</span>
                  </button>
                  <div className={`grid transition-all duration-300 ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                    <div className="overflow-hidden"><p className="px-6 pb-6 text-slate-600 leading-relaxed">{e.body}</p></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="deep-section">
        <div className="container-x py-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <h2 className="text-white text-2xl md:text-3xl text-balance" style={{ fontWeight: 600 }}>Still have questions? We're here to help.</h2>
          <a href={`tel:${CLINIC.phoneRaw}`} className="btn-coral inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold"><Phone size={18} /> {CLINIC.phone}</a>
        </div>
      </section>
    </div>
  );
}
